import { ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import { addDays, addMonths, addWeeks, format } from 'date-fns';
import * as Popover from '@radix-ui/react-popover';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import {
    GANTT_TIME_SCALES,
    type GanttTimeScale,
} from './gantt-config';
import { cn } from '@/lib/utils';

// ============================================
// GANTT TOOLBAR
// ============================================
//
// t17: small action row above the Gantt timeline. Prev / Next / Today
// on the left, the current title in the middle, the time-scale picker
// + Settings popover trigger on the right.
//
// R7 / t33: the Settings cog is now <Popover.Trigger asChild> so it
// implicitly registers itself as the Radix anchor for the settings
// panel rendered by GanttView (inside the same <Popover.Root>). The
// previous "forward a ref + read button.getBoundingClientRect() in the
// panel" hack was unreliable because Radix Popper reads its anchor
// from the PopperContext set by Trigger / Anchor, NOT from a Content
// prop — it silently fell back to off-screen positioning.

export interface GanttToolbarProps {
    /** The first day visible in the timeline (anchor). */
    origin: Date;
    /**
     * R8 / t34: the *requested* time scale (may be 'auto'). Used to
     * decide which tab is `aria-selected` / `data-state="active"` so
     * the Auto button stays selected while the concrete scale (see
     * `timeScale` below) auto-resolves to a different concrete value.
     * Previously the toolbar only saw the concrete scale, so
     * clicking Auto appeared to leave the active tab unchanged.
     */
    requestedTimeScale: GanttTimeScale;
    /** The concrete resolved time scale (auto already resolved by the parent). */
    timeScale: Exclude<GanttTimeScale, 'auto'>;
    /** Whether the project has a Sprint custom field. */
    hasSprintField: boolean;
    onOriginChange: (next: Date) => void;
    onTimeScaleChange: (scale: GanttTimeScale) => void;
}

function shiftOrigin(
    origin: Date,
    timeScale: Exclude<GanttTimeScale, 'auto'>,
    direction: 1 | -1
): Date {
    switch (timeScale) {
        case 'day':
            return addDays(origin, 7 * direction);
        case 'week':
            return addWeeks(origin, 4 * direction);
        case 'month':
            return addMonths(origin, 1 * direction);
    }
}

function originTitle(
    origin: Date,
    timeScale: Exclude<GanttTimeScale, 'auto'>,
    t: (k: any) => string
): string {
    if (timeScale === 'day') {
        return `${format(origin, 'MMM d')} – ${format(addDays(origin, 27), 'MMM d, yyyy')}`;
    }
    if (timeScale === 'week') {
        return `${format(origin, 'MMM d')} – ${format(addWeeks(origin, 11), 'MMM d, yyyy')}`;
    }
    return `${format(origin, 'MMM yyyy')} – ${format(addMonths(origin, 5), 'MMM yyyy')}`;
}

export function GanttToolbar({
    origin,
    requestedTimeScale,
    timeScale,
    hasSprintField: _hasSprintField,
    onOriginChange,
    onTimeScaleChange,
}: GanttToolbarProps) {
    const { t } = useI18n();

    const title = originTitle(origin, timeScale, t);

    return (
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2 mb-3">
            <div className="flex items-center gap-1 flex-wrap">
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 cursor-pointer"
                    onClick={() => onOriginChange(shiftOrigin(origin, timeScale, -1))}
                    aria-label={t('gantt.previous')}
                    data-testid="gantt-toolbar-prev"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 cursor-pointer"
                    onClick={() => onOriginChange(new Date())}
                    data-testid="gantt-toolbar-today"
                >
                    {t('common.today')}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 cursor-pointer"
                    onClick={() => onOriginChange(shiftOrigin(origin, timeScale, 1))}
                    aria-label={t('gantt.next')}
                    data-testid="gantt-toolbar-next"
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="ml-2 text-sm font-medium text-foreground capitalize hidden sm:inline">
                    {title}
                </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <div
                    role="tablist"
                    aria-label={t('gantt.timeScaleLabel')}
                    className="inline-flex items-center rounded-md border border-white/10 bg-white/5 p-0.5"
                >
                    {GANTT_TIME_SCALES.map((scale) => (
                        <button
                            key={scale}
                            type="button"
                            role="tab"
                            aria-selected={requestedTimeScale === scale}
                            data-state={requestedTimeScale === scale ? 'active' : 'inactive'}
                            data-testid={`gantt-toolbar-${scale}`}
                            onClick={() => onTimeScaleChange(scale)}
                            className={cn(
                                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium transition-colors cursor-pointer select-none',
                                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/50',
                                requestedTimeScale === scale
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-foreground-muted hover:text-foreground hover:bg-white/5'
                            )}
                        >
                            {t(`gantt.timeScale.${scale}`)}
                        </button>
                    ))}
                </div>
                {/* R7 / t33: <Popover.Trigger asChild> opens the settings
                    panel rendered by GanttView (same <Popover.Root>) and
                    registers the button as the panel's anchor. */}
                <Popover.Trigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 cursor-pointer"
                        aria-label={t('gantt.settings')}
                        title={t('gantt.settings')}
                        data-testid="gantt-toolbar-cog"
                    >
                        <Settings className="h-4 w-4" />
                    </Button>
                </Popover.Trigger>
            </div>
        </div>
    );
}
